import { useCallback, useEffect, useRef, useState } from 'react'
import type { TutorialModule, TutorialValidationState, TutorialSetupActions, TutorialStep } from '../types'

interface UseTutorialOptions {
  modules: TutorialModule[]
  enabled: boolean
  autoStart: boolean
  validationState: TutorialValidationState
  setupActions: TutorialSetupActions
}

const HINT_DELAY = 5000

export function useTutorial({
  modules,
  enabled,
  autoStart,
  validationState,
  setupActions,
}: UseTutorialOptions) {
  const [isActive, setIsActive] = useState(false)
  const [moduleIndex, setModuleIndex] = useState(0)
  const [stepIndex, setStepIndex] = useState(0)
  const [showHint, setShowHint] = useState(false)
  const [stepPassed, setStepPassed] = useState(false)
  const hintTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const currentModule = isActive ? (modules[moduleIndex] ?? null) : null
  const currentStep: TutorialStep | null = currentModule?.steps[stepIndex] ?? null

  // Auto-start
  useEffect(() => {
    if (enabled && autoStart && modules.length > 0 && !isActive) {
      setIsActive(true)
      setModuleIndex(0)
      setStepIndex(0)
    }
  }, [enabled, autoStart, modules.length])

  // Set problem when module changes
  useEffect(() => {
    if (!isActive || !currentModule) return
    setupActions.showProblem(currentModule.problem)
    setupActions.resetSides()
  }, [isActive, moduleIndex])

  // Run autoSetup when step changes
  useEffect(() => {
    if (!isActive || !currentStep) return
    currentStep.autoSetup?.(setupActions)
  }, [isActive, moduleIndex, stepIndex])

  // Hint timer: show hint after delay if step not yet passed
  useEffect(() => {
    setShowHint(false)
    if (hintTimer.current) clearTimeout(hintTimer.current)
    if (isActive && currentStep?.hint) {
      hintTimer.current = setTimeout(() => setShowHint(true), HINT_DELAY)
    }
    return () => {
      if (hintTimer.current) clearTimeout(hintTimer.current)
    }
  }, [isActive, moduleIndex, stepIndex])

  // Advance to next step/module
  const advance = useCallback(() => {
    if (!currentModule) return
    if (stepIndex < currentModule.steps.length - 1) {
      setStepIndex(prev => prev + 1)
    } else if (moduleIndex < modules.length - 1) {
      setModuleIndex(prev => prev + 1)
      setStepIndex(0)
    } else {
      setIsActive(false)
    }
    setStepPassed(false)
  }, [currentModule, stepIndex, moduleIndex, modules.length])

  // Auto-validate on state changes
  useEffect(() => {
    if (!isActive || !currentStep || stepPassed) return
    if (currentStep.validate(validationState)) {
      setStepPassed(true)
      if (hintTimer.current) clearTimeout(hintTimer.current)
      // 마지막 스텝이면 자동 advance 안 함 → 완료 화면 유지
      const isLastStep = currentModule
        && stepIndex === currentModule.steps.length - 1
        && moduleIndex === modules.length - 1
      if (!isLastStep) {
        advanceTimer.current = setTimeout(() => advance(), 800)
      }
    }
  }, [validationState, isActive, currentStep, stepPassed, advance, currentModule, stepIndex, moduleIndex, modules.length])

  const start = useCallback(() => {
    if (modules.length === 0) return
    setIsActive(true)
    setModuleIndex(0)
    setStepIndex(0)
    setStepPassed(false)
  }, [modules.length])

  const skip = useCallback(() => {
    if (moduleIndex < modules.length - 1) {
      setModuleIndex(prev => prev + 1)
      setStepIndex(0)
      setStepPassed(false)
    } else {
      setIsActive(false)
    }
  }, [moduleIndex, modules.length])

  const stop = useCallback(() => {
    setIsActive(false)
    if (hintTimer.current) clearTimeout(hintTimer.current)
    if (advanceTimer.current) clearTimeout(advanceTimer.current)
  }, [])

  // Cleanup
  useEffect(() => {
    return () => {
      if (hintTimer.current) clearTimeout(hintTimer.current)
      if (advanceTimer.current) clearTimeout(advanceTimer.current)
    }
  }, [])

  // Progress
  const totalSteps = modules.reduce((sum, m) => sum + m.steps.length, 0)
  let completedSteps = 0
  for (let i = 0; i < moduleIndex; i++) {
    completedSteps += modules[i].steps.length
  }
  completedSteps += stepIndex

  return {
    isActive,
    currentModule,
    currentStep,
    moduleIndex,
    stepIndex,
    showHint,
    stepPassed,
    totalModules: modules.length,
    totalSteps,
    completedSteps,
    start,
    skip,
    stop,
  }
}
